import { Form, Input, Select } from "antd";
import { AppButton } from "components/button/AppButton";
import avatar from "assets/images/default.webp";
import {
  textInputValidationRules,
  phoneNumberValidationRule,
} from "utils/formHelpers/validations";
import {
  QUERY_KEY_FOR_RECRUITER_PROFILE,
  useGetRecruiterProfile,
} from "../hooks/useGetRecruiterProfile";
import { ChangeEvent, useEffect, useState } from "react";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useUpdateRecruiterProfile } from "../hooks/useUpdateRecruiterProfile";
import { openNotification } from "utils/notification";
import { useQueryClient } from "react-query";
import { IRecruiterProfile } from "../types";
import { useSignIn } from "react-auth-kit";
import { FileWithPreview } from "features/settings/types";
import { resizeImageToSquare, resizeCoverPhoto } from "utils/resizephoto";

const DEFAULT_COVER = "/assets/cover_image.png";

export const Profile = () => {
  const { data: profileData, isLoading } = useGetRecruiterProfile();
  const { userInfo, token } = useGetUserInfo();
  const [form] = Form.useForm();
  const { mutate, isLoading: loadUpdate } = useUpdateRecruiterProfile();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<FileWithPreview | null>(null);
  const [resizedBlob, setResizedBlob] = useState<Blob | null>(null);
  const [coverPhotoBlob, setCoverPhotoBlob] = useState<Blob | null>(null);
  const signIn = useSignIn();

  useEffect(() => {
    form.setFieldsValue({
      first_name: profileData?.data.first_name,
      middle_name: profileData?.data.middle_name,
      last_name: profileData?.data.last_name,
      phone: profileData?.data.phone,
    });
  }, [form, profileData]);

  const handleSubmit = (data: any) => {
    const props: IRecruiterProfile = {
      first_name: data.first_name,
      middle_name: data.middle_name,
      last_name: data.last_name,
      company_name: profileData?.data.company_name || "",
      rc_number: profileData?.data.rc_number || "",
      photo: resizedBlob ? resizedBlob : null,
      cover_photo: coverPhotoBlob ? coverPhotoBlob : null,
      phone: data.phone ? data.phone : "",
      country: profileData?.data.country ? profileData.data.country : "",
      city: profileData?.data.city ? profileData?.data.city : "",
      sector_id: profileData?.data.sector_id ? profileData.data.sector_id : "",
      language: profileData?.data.language ? profileData.data.language : "",
      prefered_industries: profileData?.data.prefered_industries,
      prefered_locations: profileData?.data.prefered_locations,
      company_website: profileData?.data.company_website
        ? profileData.data.company_website
        : "",
      linkedin: profileData?.data.linkedin ? profileData.data.linkedin : "",
      github: profileData?.data.github ? profileData.data.github : "",
      behance: profileData?.data.behance ? profileData.data.behance : "",
      facebook: profileData?.data.facebook ? profileData.data.facebook : "",
      x: profileData?.data.x ? profileData.data.x : "",
      instagram: profileData?.data.instagram ? profileData.data.instagram : "",
      short_bio: profileData?.data.short_bio ? profileData.data.short_bio : "",
      company_size: profileData?.data.company_size
        ? profileData.data.company_size
        : "",
      token,
    };

    mutate(props, {
      onError: (err: any) => {
        openNotification({
          title: "Error",
          state: "error",
          description: "Error occurred try again",
          duration: 4.5,
        });
      },
      onSuccess: (res: any) => {
        openNotification({
          title: "Success",
          state: "success",
          description: "Profile Updated Successfully",
          duration: 4.5,
        });
        // form.resetFields();
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FOR_RECRUITER_PROFILE],
        });
      },
    });
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile({
        ...selectedFile,
        preview: URL.createObjectURL(selectedFile),
      } as FileWithPreview);
      try {
        const resizedImageBlob = await resizeImageToSquare(selectedFile, 200); // Resize image to a 200x200 square
        setResizedBlob(resizedImageBlob);
      } catch (error) {
        console.error("Error resizing image:", error);
      }
    }
  };

  const coverPhotoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile({
        ...selectedFile,
        preview: URL.createObjectURL(selectedFile),
      } as FileWithPreview);
      try {
        const resizedImageBlob = await resizeCoverPhoto(selectedFile);
        setCoverPhotoBlob(resizedImageBlob);
      } catch (error) {
        console.error("Error resizing image:", error);
      }
    }
  };

  let corporate_id = null;

  if (userInfo?.recruiter && userInfo?.recruiter.is_corporate === 1) {
    corporate_id = userInfo?.current_team
      ? userInfo?.current_team.corporate_id
      : userInfo?.recruiter.is_corporate === 1
        ? userInfo?.recruiter.id
        : "";
  }

  return (
    <>
      <Form form={form} disabled={isLoading} onFinish={handleSubmit}>
        <div className="flex justify-between mb-4">
          <div>
            <h1 className="font-semibold text-lg text-accent">Personal info</h1>
            <h3 className="text-sm">Update your details here.</h3>
          </div>
        </div>
        <hr />

        <div className="grid grid-cols-1 md:grid-cols-5 my-3 md:my-5 gap-3">
          <Form.Item noStyle name="image">
            <Input type="hidden" />
          </Form.Item>

          <div className="relative col-span-5">
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              id="fileInput"
              onChange={coverPhotoChange}
            />
            <i
              className="ri-edit-2-fill absolute top-2 right-2 p-2 bg-white rounded-full cursor-pointer text-primary hover:text-primary"
              onClick={() => document.getElementById("fileInput")?.click()}
            ></i>
            <img
              src={
                coverPhotoBlob
                  ? URL.createObjectURL(coverPhotoBlob)
                  : profileData?.data.user.cover_photo || DEFAULT_COVER
              }
              alt=""
              className="w-full h-full object-cover"
            />

            <div className="absolute -bottom-6 left-4">
              <div className="relative w-fit">
                <img
                  src={
                    resizedBlob
                      ? URL.createObjectURL(resizedBlob)
                      : profileData?.data.dp ?? avatar
                  }
                  alt="avatar"
                  className="h-[6.5rem] w-[6.5rem] rounded-full border-2 border-white shadow-lg"
                />

                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  id="cover_photo"
                  onChange={handleFileChange}
                />

                {/* Edit icon touching image */}
                <i
                  className="ri-edit-2-fill absolute -top-1 -right-1 bg-white rounded-full p-1 shadow 
              cursor-pointer text-primary hover:text-primary"
                  onClick={() =>
                    document.getElementById("cover_photo")?.click()
                  }
                ></i>
              </div>
            </div>

            <h3 className="font-medium mt-10"></h3>
            <h3 className="text-xs"></h3>
          </div>

          <Form.Item noStyle name="image">
            <Input type="hidden" />
          </Form.Item>
        </div>
        <br />
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-6 p-4 rounded shadow-lg w-full">
            <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <h3>Full Name</h3>
              <div className="col-span-3">
                <div className="flex gap-x-3">
                  <Form.Item name="first_name" rules={textInputValidationRules}>
                    <Input placeholder="First name" disabled />
                  </Form.Item>
                  <Form.Item
                    name="middle_name"
                    rules={textInputValidationRules}
                  >
                    <Input placeholder="Middle name" disabled />
                  </Form.Item>
                  <Form.Item name="last_name" rules={textInputValidationRules}>
                    <Input placeholder="Last name" disabled />
                  </Form.Item>
                </div>
              </div>
            </div>
            <hr />
            <div className="grid grid-cols-5 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <span>
                <h3>Email Address</h3>
              </span>
              <span className="col-span-4 w-full">
                <Form.Item
                  // name="email"
                  className="col-span-2"
                >
                  <Input
                    placeholder={userInfo?.email}
                    disabled
                    className="w-full"
                  />
                </Form.Item>
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <h3>Phone Number</h3>
              <span className="col-span-4 w-full">
                <Form.Item
                  name="phone"
                  className="col-span-2"
                  rules={phoneNumberValidationRule}
                >
                  <Input placeholder="+2348012345678" />
                </Form.Item>
              </span>
            </div>

            <div className="flex justify-between mb-4 mt-4">
              <div>
                <h1 className="font-semibold text-lg text-accent"></h1>
              </div>
              <div className="flex items-center gap-x-3">
                <AppButton
                  type="submit"
                  label="Save"
                  isLoading={loadUpdate}
                  additionalClassNames={["button w-full py-1"]}
                />
              </div>
            </div>
          </div>
        </div>
        <br />
        <hr />
      </Form>
    </>
  );
};
